/* eslint-disable react-refresh/only-export-components */
import ClearIcon from "@mui/icons-material/Clear";
import DragIndicator from "@mui/icons-material/DragIndicator";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Paper,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { type DragEvent, useCallback, useMemo, useState } from "react";

export type ScoutDashboardChartConfig = {
  chartOrder: string[];
  hiddenChartIds: string[];
};

export type ScoutDashboardChartSettings = {
  current: ScoutDashboardChartConfig;
  savedConfigs: Record<string, ScoutDashboardChartConfig>;
  activeConfigName: string | null;
};

export type ScoutDashboardChartOption = {
  id: string;
  title: string;
};

export const DEFAULT_SCOUT_DASHBOARD_CHART_SETTINGS: ScoutDashboardChartSettings = {
  current: {
    chartOrder: [],
    hiddenChartIds: [],
  },
  savedConfigs: {},
  activeConfigName: null,
};

export function getScoutDashboardChartStorageKey(userEmail?: string | null): string {
  return `scout-dashboard-chart-settings|${userEmail ?? "anonymous"}`;
}

export function createDefaultScoutDashboardChartConfig(
  chartIds: string[]
): ScoutDashboardChartConfig {
  return {
    chartOrder: [...chartIds],
    hiddenChartIds: [],
  };
}

/**
 * Reconciles a stored chart configuration with the chart IDs currently returned
 * by the dashboard query.
 *
 * Existing known chart IDs keep their saved order, newly added charts are appended
 * in API order, and removed chart IDs are dropped from both order and visibility.
 */
export function normalizeScoutDashboardChartConfig(
  config: ScoutDashboardChartConfig | undefined,
  chartIds: string[]
): ScoutDashboardChartConfig {
  const chartIdSet = new Set(chartIds);
  const seenOrderedIds = new Set<string>();
  const chartOrder: string[] = [];

  for (const chartId of config?.chartOrder ?? []) {
    if (chartIdSet.has(chartId) && !seenOrderedIds.has(chartId)) {
      chartOrder.push(chartId);
      seenOrderedIds.add(chartId);
    }
  }

  for (const chartId of chartIds) {
    if (!seenOrderedIds.has(chartId)) {
      chartOrder.push(chartId);
      seenOrderedIds.add(chartId);
    }
  }

  const hiddenChartIdSet = new Set(config?.hiddenChartIds ?? []);
  const hiddenChartIds = chartIds.filter((chartId) => hiddenChartIdSet.has(chartId));

  return {
    chartOrder,
    hiddenChartIds,
  };
}

export function areScoutDashboardChartConfigsEqual(
  firstConfig: ScoutDashboardChartConfig,
  secondConfig: ScoutDashboardChartConfig
): boolean {
  if (firstConfig.chartOrder.length !== secondConfig.chartOrder.length) {
    return false;
  }

  if (firstConfig.hiddenChartIds.length !== secondConfig.hiddenChartIds.length) {
    return false;
  }

  return (
    firstConfig.chartOrder.every((chartId, index) => secondConfig.chartOrder[index] === chartId) &&
    firstConfig.hiddenChartIds.every(
      (chartId, index) => secondConfig.hiddenChartIds[index] === chartId
    )
  );
}

/**
 * Normalizes the current and saved chart configurations against the live chart
 * set, and clears the active saved configuration when the current layout no
 * longer matches it.
 */
export function normalizeScoutDashboardChartSettings(
  settings: ScoutDashboardChartSettings,
  chartIds: string[]
): ScoutDashboardChartSettings {
  const current = normalizeScoutDashboardChartConfig(settings.current, chartIds);
  const savedConfigs: Record<string, ScoutDashboardChartConfig> = {};

  Object.entries(settings.savedConfigs).forEach(([name, config]) => {
    savedConfigs[name] = normalizeScoutDashboardChartConfig(config, chartIds);
  });

  const activeConfig =
    settings.activeConfigName === null ? undefined : savedConfigs[settings.activeConfigName];
  const activeConfigName =
    settings.activeConfigName !== null &&
    activeConfig &&
    areScoutDashboardChartConfigsEqual(current, activeConfig)
      ? settings.activeConfigName
      : null;

  return {
    current,
    savedConfigs,
    activeConfigName,
  };
}

export function setCurrentScoutDashboardChartConfig(
  settings: ScoutDashboardChartSettings,
  nextConfig: ScoutDashboardChartConfig,
  chartIds: string[]
): ScoutDashboardChartSettings {
  return normalizeScoutDashboardChartSettings(
    {
      ...settings,
      current: nextConfig,
    },
    chartIds
  );
}

export function saveScoutDashboardChartConfig(
  settings: ScoutDashboardChartSettings,
  name: string,
  chartIds: string[]
): ScoutDashboardChartSettings {
  const normalizedCurrent = normalizeScoutDashboardChartConfig(settings.current, chartIds);
  return normalizeScoutDashboardChartSettings(
    {
      ...settings,
      current: normalizedCurrent,
      savedConfigs: {
        ...settings.savedConfigs,
        [name]: normalizedCurrent,
      },
      activeConfigName: name,
    },
    chartIds
  );
}

export function deleteScoutDashboardChartConfig(
  settings: ScoutDashboardChartSettings,
  name: string,
  chartIds: string[]
): ScoutDashboardChartSettings {
  const savedConfigs = { ...settings.savedConfigs };
  delete savedConfigs[name];

  return normalizeScoutDashboardChartSettings(
    {
      ...settings,
      savedConfigs,
      activeConfigName: settings.activeConfigName === name ? null : settings.activeConfigName,
    },
    chartIds
  );
}

export function selectScoutDashboardChartConfig(
  settings: ScoutDashboardChartSettings,
  name: string,
  chartIds: string[]
): ScoutDashboardChartSettings {
  const selectedConfig = settings.savedConfigs[name];
  if (!selectedConfig) {
    return normalizeScoutDashboardChartSettings(settings, chartIds);
  }

  const current = normalizeScoutDashboardChartConfig(selectedConfig, chartIds);
  return normalizeScoutDashboardChartSettings(
    {
      ...settings,
      current,
      activeConfigName: name,
    },
    chartIds
  );
}

export function reorderScoutDashboardChartIds(
  chartOrder: string[],
  draggedChartId: string,
  targetChartId: string
): string[] {
  const oldIndex = chartOrder.indexOf(draggedChartId);
  const targetIndex = chartOrder.indexOf(targetChartId);

  if (oldIndex === -1 || targetIndex === -1 || oldIndex === targetIndex) {
    return [...chartOrder];
  }

  const nextOrder = chartOrder.filter((chartId) => chartId !== draggedChartId);
  const insertIndex = Math.min(targetIndex, nextOrder.length);
  nextOrder.splice(insertIndex, 0, draggedChartId);
  return nextOrder;
}

export function getVisibleScoutDashboardChartItems<TItem extends { id: string }>(
  items: TItem[],
  config: ScoutDashboardChartConfig
): TItem[] {
  const itemsById = new Map(items.map((item) => [item.id, item]));
  const hiddenChartIdSet = new Set(config.hiddenChartIds);
  const visibleItems: TItem[] = [];

  for (const chartId of config.chartOrder) {
    const item = itemsById.get(chartId);
    if (item && !hiddenChartIdSet.has(chartId)) {
      visibleItems.push(item);
    }
  }

  return visibleItems;
}

export function ManageScoutChartsPopover({
  open,
  anchorEl,
  onClose,
  charts,
  currentConfig,
  savedConfigs,
  activeConfigName,
  onCurrentConfigChange,
  onSaveConfig,
  onDeleteConfig,
  onSelectConfig,
  onResetConfig,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  charts: ScoutDashboardChartOption[];
  currentConfig: ScoutDashboardChartConfig;
  savedConfigs: Record<string, ScoutDashboardChartConfig>;
  activeConfigName: string | null;
  onCurrentConfigChange: (config: ScoutDashboardChartConfig) => void;
  onSaveConfig: (name: string) => void;
  onDeleteConfig: (name: string) => void;
  onSelectConfig: (name: string) => void;
  onResetConfig: () => void;
}) {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [saveAsConfig, setSaveAsConfig] = useState(false);
  const [configName, setConfigName] = useState("");
  const [expandSavedConfigs, setExpandSavedConfigs] = useState(false);
  const [draggingChartId, setDraggingChartId] = useState<string | null>(null);
  const [dragPreviewChartOrder, setDragPreviewChartOrder] = useState<string[] | null>(null);
  const normalizedConfig = useMemo(
    () =>
      normalizeScoutDashboardChartConfig(
        currentConfig,
        charts.map((chart) => chart.id)
      ),
    [charts, currentConfig]
  );
  const chartById = useMemo(() => new Map(charts.map((chart) => [chart.id, chart])), [charts]);
  const displayedChartOrder = dragPreviewChartOrder ?? normalizedConfig.chartOrder;
  const orderedCharts = useMemo(() => {
    const nextCharts: ScoutDashboardChartOption[] = [];

    for (const chartId of displayedChartOrder) {
      const chart = chartById.get(chartId);
      if (chart) {
        nextCharts.push(chart);
      }
    }

    return nextCharts;
  }, [chartById, displayedChartOrder]);
  const filteredCharts = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    if (!normalizedSearch) {
      return orderedCharts;
    }

    return orderedCharts.filter((chart) =>
      [chart.title, chart.id].join(" ").toLowerCase().includes(normalizedSearch)
    );
  }, [orderedCharts, searchQuery]);
  const hiddenChartIdSet = useMemo(
    () => new Set(normalizedConfig.hiddenChartIds),
    [normalizedConfig.hiddenChartIds]
  );
  const isSearchActive = searchQuery.trim().length > 0;
  const allSelected = filteredCharts.every((chart) => !hiddenChartIdSet.has(chart.id));
  const hasVisibleFilteredChart = filteredCharts.some((chart) => !hiddenChartIdSet.has(chart.id));
  const hasHiddenFilteredChart = filteredCharts.some((chart) => hiddenChartIdSet.has(chart.id));
  const indeterminate = hasVisibleFilteredChart && hasHiddenFilteredChart;
  const savedConfigNames = useMemo(() => Object.keys(savedConfigs).sort(), [savedConfigs]);
  const trimmedConfigName = configName.trim();
  const isDuplicateConfigName =
    trimmedConfigName.length > 0 && Boolean(savedConfigs[trimmedConfigName]);
  const savedConfigChipColor = theme.palette.encampBlue?.main ?? theme.palette.primary.main;
  const defaultConfig = useMemo(
    () => createDefaultScoutDashboardChartConfig(charts.map((chart) => chart.id)),
    [charts]
  );

  const updateHiddenChartIds = useCallback(
    (hiddenChartIds: string[]) => {
      onCurrentConfigChange({
        chartOrder: normalizedConfig.chartOrder,
        hiddenChartIds,
      });
    },
    [normalizedConfig.chartOrder, onCurrentConfigChange]
  );

  const handleToggleChart = useCallback(
    (chartId: string) => {
      const nextHiddenChartIdSet = new Set(normalizedConfig.hiddenChartIds);
      if (nextHiddenChartIdSet.has(chartId)) {
        nextHiddenChartIdSet.delete(chartId);
      } else {
        nextHiddenChartIdSet.add(chartId);
      }

      updateHiddenChartIds(
        normalizedConfig.chartOrder.filter((orderedChartId) =>
          nextHiddenChartIdSet.has(orderedChartId)
        )
      );
    },
    [normalizedConfig.chartOrder, normalizedConfig.hiddenChartIds, updateHiddenChartIds]
  );

  const handleToggleAll = useCallback(() => {
    const filteredChartIds = new Set(filteredCharts.map((chart) => chart.id));
    const nextHiddenChartIdSet = new Set(normalizedConfig.hiddenChartIds);
    const shouldShowFilteredCharts = !allSelected;

    filteredChartIds.forEach((chartId) => {
      if (shouldShowFilteredCharts) {
        nextHiddenChartIdSet.delete(chartId);
      } else {
        nextHiddenChartIdSet.add(chartId);
      }
    });

    updateHiddenChartIds(
      normalizedConfig.chartOrder.filter((chartId) => nextHiddenChartIdSet.has(chartId))
    );
  }, [
    allSelected,
    filteredCharts,
    normalizedConfig.chartOrder,
    normalizedConfig.hiddenChartIds,
    updateHiddenChartIds,
  ]);

  const handleDragStart = useCallback(
    (event: DragEvent<HTMLButtonElement>, chartId: string) => {
      if (isSearchActive) {
        event.preventDefault();
        return;
      }

      setDraggingChartId(chartId);
      setDragPreviewChartOrder(normalizedConfig.chartOrder);
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", chartId);
    },
    [isSearchActive, normalizedConfig.chartOrder]
  );

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!draggingChartId || isSearchActive) {
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    },
    [draggingChartId, isSearchActive]
  );

  const moveDraggedChart = useCallback(
    (targetChartId: string) => {
      if (!draggingChartId || isSearchActive || draggingChartId === targetChartId) {
        return;
      }

      const chartOrderToReorder = dragPreviewChartOrder ?? normalizedConfig.chartOrder;
      const chartOrder = reorderScoutDashboardChartIds(
        chartOrderToReorder,
        draggingChartId,
        targetChartId
      );

      setDragPreviewChartOrder(chartOrder);
    },
    [dragPreviewChartOrder, draggingChartId, isSearchActive, normalizedConfig.chartOrder]
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      event.preventDefault();

      if (dragPreviewChartOrder) {
        const hasChartOrderChanged =
          dragPreviewChartOrder.length !== normalizedConfig.chartOrder.length ||
          dragPreviewChartOrder.some(
            (chartId, index) => normalizedConfig.chartOrder[index] !== chartId
          );

        if (hasChartOrderChanged) {
          onCurrentConfigChange({
            chartOrder: dragPreviewChartOrder,
            hiddenChartIds: normalizedConfig.hiddenChartIds,
          });
        }
      }

      setDraggingChartId(null);
      setDragPreviewChartOrder(null);
    },
    [
      dragPreviewChartOrder,
      normalizedConfig.chartOrder,
      normalizedConfig.hiddenChartIds,
      onCurrentConfigChange,
    ]
  );

  const handleDragEnd = useCallback(() => {
    setDraggingChartId(null);
    setDragPreviewChartOrder(null);
  }, []);

  const handleSaveConfig = useCallback(() => {
    if (!trimmedConfigName) {
      return;
    }

    onSaveConfig(trimmedConfigName);
    setSaveAsConfig(false);
    setConfigName("");
    setExpandSavedConfigs(true);
  }, [onSaveConfig, trimmedConfigName]);

  const handleClose = useCallback(() => {
    setSaveAsConfig(false);
    setConfigName("");
    setDraggingChartId(null);
    setDragPreviewChartOrder(null);
    onClose();
  }, [onClose]);

  const isDefaultConfig = areScoutDashboardChartConfigsEqual(normalizedConfig, defaultConfig);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: 340,
          maxHeight: 560,
        }}
      >
        <Box sx={{ px: 1, pt: 1, pb: 0.5, borderBottom: 1, borderColor: "divider" }}>
          <TextField
            size="small"
            placeholder="Search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")} edge="end">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}>
          <Chip
            label="Saved configurations"
            onClick={() => setExpandSavedConfigs(!expandSavedConfigs)}
            onDelete={() => setExpandSavedConfigs(!expandSavedConfigs)}
            deleteIcon={expandSavedConfigs ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              backgroundColor: savedConfigNames.length > 0 ? savedConfigChipColor : undefined,
              color: savedConfigNames.length > 0 ? "white" : undefined,
              height: 28,
              "& .MuiChip-deleteIcon": {
                color: savedConfigNames.length > 0 ? "white" : theme.palette.text.secondary,
              },
              "&:hover": {
                backgroundColor: savedConfigNames.length > 0 ? "#4568B0" : undefined,
              },
            }}
          />

          <Collapse in={expandSavedConfigs}>
            <Paper
              variant="outlined"
              sx={{
                mt: 1.5,
                maxHeight: 136,
                overflow: "auto",
                backgroundColor: "white",
              }}
            >
              {savedConfigNames.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2, textAlign: "center" }}
                >
                  No saved configurations yet
                </Typography>
              ) : (
                <Box>
                  {savedConfigNames.map((name) => (
                    <Box
                      key={name}
                      onClick={() => onSelectConfig(name)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: 34,
                        cursor: "pointer",
                        backgroundColor:
                          activeConfigName === name ? "action.selected" : "transparent",
                        borderBottom: 1,
                        borderColor: "divider",
                        "&:last-child": {
                          borderBottom: 0,
                        },
                        "&:hover": {
                          backgroundColor:
                            activeConfigName === name ? "action.selected" : "action.hover",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        {name}
                      </Typography>
                      <Box
                        sx={{
                          width: 44,
                          flexShrink: 0,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          aria-label={`Delete ${name} chart configuration`}
                          onClick={(event) => {
                            event.stopPropagation();
                            onDeleteConfig(name);
                          }}
                          sx={{ padding: "5px" }}
                        >
                          <RemoveIcon
                            sx={{
                              fontSize: 20,
                              color: "action.disabled",
                            }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Collapse>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {searchQuery.trim() && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 2, py: 0.5, display: "block" }}
            >
              Showing {filteredCharts.length} of {orderedCharts.length} charts
            </Typography>
          )}
          {filteredCharts.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, py: 1, display: "block" }}
            >
              No charts match your search
            </Typography>
          ) : (
            <List onDragOver={handleDragOver} onDrop={handleDrop} sx={{ py: 0 }}>
              {filteredCharts.map((chart) => (
                <ListItem
                  key={chart.id}
                  onDragEnter={() => moveDraggedChart(chart.id)}
                  onDragOver={handleDragOver}
                  sx={{
                    p: 0,
                    pl: 0.5,
                    pr: 1,
                    opacity: draggingChartId === chart.id ? 0.55 : 1,
                    transition: "background-color 120ms ease, opacity 120ms ease",
                    "&:hover": {
                      backgroundColor: draggingChartId ? "transparent" : "action.hover",
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label={`Reorder ${chart.title}`}
                    disabled={isSearchActive}
                    draggable={!isSearchActive}
                    onDragStart={(event) => handleDragStart(event, chart.id)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      cursor: isSearchActive ? "default" : "grab",
                      color: "text.secondary",
                    }}
                  >
                    <DragIndicator fontSize="small" />
                  </IconButton>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!hiddenChartIdSet.has(chart.id)}
                        onChange={() => handleToggleChart(chart.id)}
                        size="small"
                      />
                    }
                    label={chart.title}
                    sx={{
                      width: "100%",
                      margin: 0,
                      minWidth: 0,
                      "& .MuiFormControlLabel-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: "0.875rem",
                        color: "text.secondary",
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Stack
          spacing={1}
          sx={{
            p: 1,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          {saveAsConfig && (
            <TextField
              fullWidth
              label="Configuration name"
              value={configName}
              onChange={(event) => setConfigName(event.target.value)}
              size="small"
              placeholder="Enter configuration name"
              autoFocus
              helperText={
                isDuplicateConfigName
                  ? "A configuration with this name already exists. Saving will overwrite it."
                  : undefined
              }
            />
          )}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={indeterminate}
                  onChange={handleToggleAll}
                  size="small"
                />
              }
              label="Show/Hide All"
              sx={{
                margin: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.875rem",
                  color: "text.secondary",
                },
              }}
            />
            <Button disabled={isDefaultConfig} onClick={onResetConfig} size="small">
              Reset
            </Button>
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Tooltip
              title="Saved chart configurations are stored in your browser. Clearing browser data or using a different device/browser will remove them."
              placement="top"
              arrow
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={saveAsConfig}
                    onChange={(event) => setSaveAsConfig(event.target.checked)}
                    size="small"
                  />
                }
                label="Save as configuration"
                sx={{
                  margin: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                    color: "text.secondary",
                  },
                }}
              />
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              disabled={!saveAsConfig || !trimmedConfigName}
              onClick={handleSaveConfig}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Popover>
  );
}
